import { Result, SessionProvider } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Permission, PaymentDTO, TicketDTO, SaleDTO, Res, DocumentPaymentDTO } from 'core/api';
import { addPrecondition, Cordova } from 'pos/common';

import ShowPaymentsScreen from './ShowPaymentsScreen';
import processPayment from './processPayment';
import { Message, Modal } from 'core/ui-next/modal';
import { ReverseExternalPaymentService } from 'pos/services';
import { OrderingFacade } from 'pos/facades';
import { canRefundOriginalPayments } from './canRefundOriginalPayments';


type ShowDocumentPayments = {
	getTicket: () => Promise<TicketDTO>,
	removePaymentPermission: Permission,
	addPaymentPermission: Permission,
	documentType: 'SalesOrder' | 'DeliveryNote' | 'Ticket'
	updatePayments: (payments: PaymentDTO[], prepayAmount: number) => Promise<Result<SaleDTO>>,
	printDocument: () => Promise<void>
};

export default async function showDocumentPayments(params: ShowDocumentPayments): Promise<Result> {

	if (!SessionProvider.getCurrent().AllowCashTransactions) {
		await Message.show(Res.Msg.MSG_CURRENT_POS_DOES_NOT_ALLOW_CASH_TRANSACTIONS());
		return Result.error();
	}

	const ticket = await params.getTicket();

	const addPayment = addPrecondition(
		Preconditions.ensure(params.addPaymentPermission),
		function (pendingAmount: number): Promise<Result> {
			return processPayment({
				mode: 'ADD',
				pendingAmount: pendingAmount,
				process: params.updatePayments,
				ticketGlobalId: ticket.GlobalId
			});
		});

	const removePayment = addPrecondition(
		Preconditions.ensure(params.removePaymentPermission),
		function (prePaidAmount: number): Promise<Result> {
			return processPayment({
				mode: 'REMOVE',
				pendingAmount: prePaidAmount,
				process: params.updatePayments,
				ticketGlobalId: ticket.GlobalId
			});
		});


	const refundPayment = addPrecondition(
		Preconditions.ensure(params.removePaymentPermission),
		async function (documentPayment: DocumentPaymentDTO): Promise<Result> {
			const payment = await OrderingFacade.getPayment(ticket.Id, documentPayment.Id);

			const { canRefund, reason } = canRefundOriginalPayments([payment], SessionProvider.getCurrent().CardProcessorType, Cordova.isInitialized());
			if (!canRefund) {
				await Message.show(reason);
				return Result.error();
			}

			const service = ReverseExternalPaymentService.for('PreRefund', ticket.GlobalId, [payment]);
			const refundResult = await service.refund();
			if (!refundResult.ok)
				return refundResult;

			const newPayment: PaymentDTO = {
				...refundResult.value[0],
				Amount: -refundResult.value[0].Amount,
				TipAmount: -refundResult.value[0].TipAmount
			};
			const result = await params.updatePayments([newPayment], documentPayment.Amount);

			// Aquí estamos muy jodidos, porque hemos devuelto un pago que ya no se puede volver a devolver pero no lo hemos podido guardar en Ágora. Intentamos
			// recuperar el pago. OJO el pago original va a quedar devuelto, aquí sólo le cogemos el dinero. Esto es similar a lo que hacemos con el ExternalPaymentService
			// cuando cerramos un documento y falla por lo que sea. Sólo queda rastro en la auditoría.
			if (!result.ok)
				await service.undoAll();

			return result;
		});

	return Modal.show(ShowPaymentsScreen,
		{
			initialTicket: ticket,
			documentType: params.documentType,
			reloadTicket: params.getTicket,
			addPayment: addPayment,
			removePayment: removePayment,
			refundPayment: refundPayment,
			printDocument: params.printDocument
		});
}